import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { hexToRgba } from '@flex-design-system/fx-colors';
import { styled } from '@flex-design-system/stitches-react';
import { Root, Viewport, Scrollbar, Thumb } from '@radix-ui/react-scroll-area';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { getColor } from '../../utils/colors.mjs';
import { legacyViewport } from './legacyScrollArea.vanilla.mjs';
import { fogBackgroundColorVar, fog, viewport, scrollbar, thumb } from './scrollArea.vanilla.mjs';

const ScrollAreaFog = forwardRef(function ScrollAreaFog2({
  side,
  color: colorFromProps = "white",
  className,
  style,
  ...props
}, forwardedRef) {
  const degree = useMemo(() => {
    if (side === "top") {
      return 180;
    } else if (side === "bottom") {
      return 0;
    } else if (side === "right") {
      return 270;
    } else if (side === "left") {
      return 90;
    }
  }, [side]);
  const color = getColor(colorFromProps);
  const background = useMemo(() => {
    const stopColors = [
      { color, stop: 0 },
      { color: hexToRgba(color, 0.8), stop: 36.36 },
      { color: hexToRgba(color, 0), stop: 100 }
    ];
    return `linear-gradient(${degree}deg, ${stopColors.map((stopColor) => `${stopColor.color} ${stopColor.stop}%`).join(", ")})`;
  }, [color, degree]);
  return /* @__PURE__ */ jsx(
    StyledFog,
    {
      ref: forwardedRef,
      className: classNames(fog({ side }), className),
      style: {
        ...style,
        ...assignInlineVars({ [fogBackgroundColorVar]: background })
      },
      ...props
    }
  );
});
const StyledFog = styled("div");
const ScrollAreaRoot = forwardRef(
  (props, ref) => {
    const {
      scrollHideDelay = 0,
      orientation = "vertical",
      asChild,
      children,
      type,
      ...restProps
    } = props;
    return /* @__PURE__ */ jsxs(
      StyledRoot,
      {
        "data-scope": "scroll-area",
        "data-part": "root",
        scrollHideDelay,
        type: type === "none" ? void 0 : type,
        ref,
        ...restProps,
        children: [
          children,
          orientation === "both" ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(ScrollAreaScrollbar, { orientation: "vertical", children: type !== "none" ? /* @__PURE__ */ jsx(ScrollAreaThumb, {}) : null }),
            /* @__PURE__ */ jsx(ScrollAreaScrollbar, { orientation: "horizontal", children: type !== "none" ? /* @__PURE__ */ jsx(ScrollAreaThumb, {}) : null })
          ] }) : /* @__PURE__ */ jsx(ScrollAreaScrollbar, { orientation, children: type !== "none" ? /* @__PURE__ */ jsx(ScrollAreaThumb, {}) : null })
        ]
      }
    );
  }
);
const StyledRoot = styled(Root);
const ScrollAreaViewport_Legacy = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    StyledViewport,
    {
      ref,
      "data-scope": "scroll-area",
      "data-part": "viewport-legacy",
      className: classNames(legacyViewport, className),
      ...props
    }
  );
});
ScrollAreaViewport_Legacy.toString = () => "[data-scope='scroll-area'][data-part='viewport-legacy']";
const ScrollAreaViewport = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    StyledViewport,
    {
      ref,
      "data-scope": "scroll-area",
      "data-part": "viewport",
      className: classNames(viewport, className),
      ...props
    }
  );
});
ScrollAreaViewport.toString = () => "[data-scope='scroll-area'][data-part='viewport']";
const StyledViewport = styled(Viewport);
const ScrollAreaScrollbar = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    StyledScrollbar,
    {
      ref,
      "data-scope": "scroll-area",
      "data-part": "scrollbar",
      className: classNames(scrollbar, className),
      ...props
    }
  );
});
ScrollAreaScrollbar.toString = () => "[data-scope='scroll-area'][data-part='scrollbar']";
const StyledScrollbar = styled(Scrollbar);
const ScrollAreaThumb = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    StyledThumb,
    {
      ref,
      "data-scope": "scroll-area",
      "data-part": "thumb",
      className: classNames(thumb, className),
      ...props
    }
  );
});
ScrollAreaThumb.toString = () => "[data-scope='scroll-area'][data-part='thumb']";
const StyledThumb = styled(Thumb);
const ScrollArea = ScrollAreaRoot;
ScrollArea.toString = () => "[data-scope='scroll-area'][data-part='root']";
ScrollArea.Viewport = ScrollAreaViewport;
ScrollArea.Viewport_Legacy = ScrollAreaViewport_Legacy;
ScrollArea.Scrollbar = ScrollAreaScrollbar;
ScrollArea.Thumb = ScrollAreaThumb;
ScrollArea.Fog = ScrollAreaFog;

export { ScrollArea };
//# sourceMappingURL=ScrollArea.mjs.map
